import { observable, action, reaction, computed } from 'mobx';
import Cookies from 'js-cookie';
import { User, client } from 'api';
import { API_URL, MOCK_ACCESS_TOKEN, ACCESS_TOKEN } from 'utils/constants';
import configStore from './ConfigStore';
import routerStore from './RouterStore';

const ONE_MONTH = 30;

class UserStore {
  storage;

  @observable token;

  @observable _userData;

  @observable fetchState;

  constructor() {
    this.storage = configStore.storage;
    this.getToken();
    this.getUserData();

    reaction(
      () => this.token,
      token => {
        if (token) {
          this.getUserData();
          Cookies.set(ACCESS_TOKEN, token, { expires: ONE_MONTH });
        } else {
          Cookies.remove(ACCESS_TOKEN);
        }
      },
    );
  }

  @computed
  get isLoggedIn() {
    return !!this.token;
  }

  @action.bound
  setToken(token) {
    this.token = token;
  }

  @action.bound
  getToken() {
    this.token = Cookies.get(ACCESS_TOKEN);
  }

  @computed
  get userData() {
    return this._userData;
  }

  setUserData(data) {
    this._userData = data;
  }

  getUserData = () => {
    if (!this._userData && this.token) {
      this.getUserDataFromStorage();
    }
  };

  @action.bound
  async getUserDataFromStorage() {
    this.fetchState = 'pending';

    try {
      const cachedUserData = await this.storage.getItem('userData');

      if (!cachedUserData) {
        this.fetchUserDataAndSaveToStorage();
      } else {
        this._userData = cachedUserData;
        this.fetchState = 'fulfilled';
      }
    } catch (e) {
      this.fetchState = 'rejected';
      console.log(`Can't fetch user data from storage`, e); // eslint-disable-line
    }
  }

  @action.bound
  async fetchUserDataAndSaveToStorage() {
    this.fetchState = 'pending';

    try {
      const response = await User.getUserData();
      this.setUserData(response.data);
      await this.storage.setItem('userData', response.data);
      this.fetchState = 'fulfilled';
    } catch (e) {
      this.fetchState = 'rejected';
      console.log(`Can't fetch user data from remote server`, e); // eslint-disable-line
    }
  }

  @action.bound
  async resetUserData() {
    try {
      await this.storage.clear();
      this.setToken(undefined);
      this.goToHome();
      this.setUserData(undefined);
    } catch (e) {
      console.log(`Can't clear user data`, e); // eslint-disable-line
    }
  }

  @action.bound
  saveUserData(data) {
    const token = data ? data.token : MOCK_ACCESS_TOKEN;
    this.setToken(token);
    client.setBaseURL(API_URL);
    this.goToHome();
  }

  goToHome = () => {
    routerStore.push('/');
  };
}

const userStore = new UserStore();

export default userStore;
